/**
 * prdTickets.js - frontend twin of proxy-server/prd/tickets.js (withTickets) plus
 * the PRD page's list coercions. Pinned by prdTicketsParity.test.js over
 * proxy-server/prd/fixtures/ticketCases.json. Never throws.
 *
 * In-memory doc: { tickets:[{surface,title,dependsOn,requirements:[{num,details}],
 * acceptanceCriteria:[string]}], analysis:{surfaces,surface,...}, ... } with NO
 * top-level requirements/acceptanceCriteria.
 */

export const SURFACES = ['api', 'client', 'web', 'other'];
export const MAX_TICKETS = 4;

const list = (a) => (Array.isArray(a) ? a : []);
const validSurface = (s) => (SURFACES.includes(s) ? s : '');

export function renumber(rows) {
  return list(rows).map((r, i) => ({ num: i + 1, details: String((r && r.details) || '') }));
}

// Old docs stored acceptance criteria as rows { num, details, status }.
export function bullets(v) {
  return list(v)
    .map((x) => (x && typeof x === 'object' ? x.details : x))
    .map((x) => String(x == null ? '' : x).trim())
    .filter(Boolean);
}

export function normalizeTickets(rawTickets, fallbackSurface) {
  const fb = validSurface(fallbackSurface) || 'other';
  return list(rawTickets).slice(0, MAX_TICKETS).map((t, i) => {
    const o = t && typeof t === 'object' ? t : {};
    const dep = Number(o.dependsOn);
    return {
      surface: validSurface(o.surface) || fb,
      title: String(o.title == null ? '' : o.title).trim(),
      // only an earlier ticket (1-based) may be depended on
      dependsOn: Number.isInteger(dep) && dep >= 1 && dep <= i ? dep : 0,
      requirements: renumber(o.requirements),
      acceptanceCriteria: bullets(o.acceptanceCriteria),
    };
  });
}

export function deriveSurfaces(tickets) {
  const out = [];
  for (const t of list(tickets)) {
    const s = t && validSurface(t.surface);
    if (s && !out.includes(s)) out.push(s);
  }
  return out.length ? out : ['other'];
}

/** Mirror of the proxy withTickets (returns tickets >= 1, derived analysis). */
export function withTickets(doc) {
  const d = doc && typeof doc === 'object' ? doc : {};
  const a = d.analysis && typeof d.analysis === 'object' ? d.analysis : {};
  const hint = (Array.isArray(a.surfaces) ? validSurface(a.surfaces[0]) : '') || validSurface(a.surface);
  let tickets;
  if (Array.isArray(d.tickets) && d.tickets.length) {
    tickets = normalizeTickets(d.tickets, hint);
  } else {
    tickets = normalizeTickets([{
      surface: hint, title: '', dependsOn: 0,
      requirements: d.requirements, acceptanceCriteria: d.acceptanceCriteria,
    }], hint);
  }
  const surfaces = deriveSurfaces(tickets);
  return { ...d, tickets, analysis: { ...a, surfaces, surface: surfaces[0] } };
}

// ---- Accept (frontend-only; no proxy twin) ----

const rowText = (r) => String(r && typeof r === 'object' ? r.details : r == null ? '' : r);
const DOC_FIELDS = ['title', 'goals', 'background'];

// Row index for a suggestion: the stated row when it still starts with the anchor,
// else the first row that does (anchor addressing, B7). Empty anchor -> stated row.
function findRow(rows, row, anchor) {
  if (!anchor) return Number.isInteger(row) && row >= 1 && row <= rows.length ? row - 1 : -1;
  if (row >= 1 && row <= rows.length && rowText(rows[row - 1]).startsWith(anchor)) return row - 1;
  return rows.findIndex((r) => rowText(r).startsWith(anchor));
}

const CHANGED = { ok: false, reason: 'target changed' };

/** Can this suggestion still be applied to the current doc? Never throws. */
export function resolveSuggestion(doc, s) {
  try {
    const d = doc && Array.isArray(doc.tickets) ? doc : { tickets: [] };
    const c = (s && s.change) || {};
    const anchor = (s && s.anchor) || '';
    const tickets = d.tickets;
    if (c.op === 'add_ticket') return tickets.length >= MAX_TICKETS ? { ok: false, reason: 'ticket limit' } : { ok: true };
    if (c.op === 'set_field') {
      if (DOC_FIELDS.includes(c.field)) return String(d[c.field] || '').startsWith(anchor) ? { ok: true } : CHANGED;
      const t = tickets[c.ticket - 1];
      if (!t) return CHANGED;
      const cur = c.field === 'ticketTitle' ? t.title : c.field === 'dependsOn' ? String(t.dependsOn) : null;
      return cur != null && String(cur).startsWith(anchor) ? { ok: true } : CHANGED;
    }
    const t = tickets[c.ticket - 1];
    if (!t || !Array.isArray(t[c.list])) return CHANGED;
    if (c.op === 'move_row' && !(c.toTicket >= 1 && c.toTicket <= tickets.length && c.toTicket !== c.ticket)) return CHANGED;
    if (c.op === 'add_row' && c.row === 0) return { ok: true };
    return findRow(t[c.list], c.row, anchor) >= 0 ? { ok: true } : CHANGED;
  } catch { return CHANGED; }
}

/** Pure. Returns the input unchanged when the suggestion does not resolve. */
export function applySuggestion(doc, s) {
  try {
    if (!resolveSuggestion(doc, s).ok) return doc;
    const c = s.change;
    const anchor = s.anchor || '';
    const d = { ...doc, tickets: doc.tickets.map((t) => ({ ...t })) };
    const mkRow = (list, text) => (list === 'requirements' ? { num: 0, details: text } : text);
    if (c.op === 'add_ticket') {
      const nt = c.newTicket || {};
      d.tickets.push({
        surface: nt.surface, title: nt.title, dependsOn: nt.dependsOn,
        requirements: list(nt.requirements).map((x) => ({ num: 0, details: String(x) })),
        acceptanceCriteria: list(nt.acceptanceCriteria),
      });
    } else if (c.op === 'set_field') {
      if (DOC_FIELDS.includes(c.field)) d[c.field] = c.value;
      else {
        const t = d.tickets[c.ticket - 1];
        if (c.field === 'ticketTitle') t.title = c.value;
        else t.dependsOn = Number(c.value);
      }
    } else {
      const t = d.tickets[c.ticket - 1];
      const rows = [...t[c.list]];
      if (c.op === 'add_row') {
        const at = c.row === 0 ? 0 : findRow(rows, c.row, anchor) + 1;
        rows.splice(at, 0, mkRow(c.list, c.value));
      } else {
        const i = findRow(rows, c.row, anchor);
        if (c.op === 'replace_row') rows[i] = mkRow(c.list, c.value);
        else {
          const [moved] = rows.splice(i, 1);
          if (c.op === 'move_row') {
            const to = d.tickets[c.toTicket - 1];
            to[c.list] = [...to[c.list], moved];
          }
        }
      }
      t[c.list] = rows;
    }
    return normalizeDoc(d);
  } catch { return doc; }
}

/** The page's in-memory doc: tickets form, list coercions, no top-level rows. */
export function normalizeDoc(prd) {
  const w = withTickets(prd);
  const t = w.backgroundTable || {};
  const ex = w.examples || {};
  const { requirements, acceptanceCriteria, ...rest } = w; // eslint-disable-line no-unused-vars
  return {
    ...rest,
    title: w.title || '',
    meta: w.meta || {},
    goals: w.goals || '',
    background: w.background || '',
    backgroundTable: { columns: list(t.columns), rows: list(t.rows) },
    examples: { label: ex.label || '', items: list(ex.items) },
    assumptions: list(w.assumptions),
    questions: list(w.questions),
  };
}
